import { motion } from 'motion/react';

interface ProjectRow {
  code: string;
  name: string;
  location: string;
  phase: string;
  progress: number;
  status: 'on-track' | 'attention' | 'paused';
  budget: string;
}

const PROJECTS: ProjectRow[] = [
  {
    code: 'CM-101',
    name: 'Scuola media — adeguamento sismico',
    location: 'Borgo Alto',
    phase: 'Strutture · P. 04 / 12',
    progress: 68,
    status: 'on-track',
    budget: '€ 1.9M / 2.8M',
  },
  {
    code: 'CM-102',
    name: 'Rete idrica — Lotto 3',
    location: 'Valverde',
    phase: 'Scavi e posa',
    progress: 41,
    status: 'attention',
    budget: '€ 2.6M / 6.3M',
  },
  {
    code: 'CM-103',
    name: 'Residenze del Parco',
    location: 'Pianello',
    phase: 'Fondazioni',
    progress: 22,
    status: 'on-track',
    budget: '€ 0.7M / 4.1M',
  },
  {
    code: 'CM-104',
    name: 'Rete gas — allacci',
    location: 'Rocca Bassa',
    phase: 'Collaudi',
    progress: 84,
    status: 'paused',
    budget: '€ 1.1M / 1.3M',
  },
];

const STATUS_LABEL: Record<ProjectRow['status'], { label: string; dot: string; text: string }> = {
  'on-track': { label: 'In linea', dot: 'bg-success', text: 'text-success' },
  attention: { label: 'Attenzione', dot: 'bg-warning', text: 'text-warning' },
  paused: { label: 'In pausa', dot: 'bg-muted-foreground', text: 'text-muted-foreground' },
};

const TABS = [
  'Dashboard',
  'Rapportino',
  'Subappalti',
  'Servizi',
  'DDT',
  'FIR',
  'Mezzi',
  'ODA',
  'Magazzino',
  'Altri costi',
  'Avanzamento economico',
];

export function ProductMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full"
    >
      <div className="relative overflow-hidden rounded-[14px] border border-input bg-card shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6),0_8px_24px_-12px_rgba(0,0,0,0.5)]">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-border bg-secondary px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
            <span className="h-2.5 w-2.5 rounded-full bg-warning" />
            <span className="h-2.5 w-2.5 rounded-full bg-success" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            boss · commessa CM-101
          </div>
          <div className="w-12" />
        </div>

        {/* Mobile — compact summary, clean and self-contained */}
        <div className="p-4 md:hidden">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              Quadro economico
            </div>
            <Pill accent>Buono</Pill>
          </div>

          <div className="mt-3 flex items-end gap-6">
            <div>
              <div className="text-[30px] font-light leading-none text-success">42,0%</div>
              <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.14em] text-muted-foreground">
                consolidato
              </div>
            </div>
            <div>
              <div className="text-[30px] font-light leading-none text-foreground">31,0%</div>
              <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.14em] text-muted-foreground">
                in corso
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <Stat label="Avanzamento" value="61%" delta="+4% sett." tone="signal" />
            <Stat label="Margine operativo" value="18,2%" delta="+1,1 pt" tone="signal" />
          </div>

          <div className="mt-4 space-y-2">
            {PROJECTS.slice(0, 2).map((p) => (
              <div key={p.code} className="rounded-lg border border-border bg-secondary/60 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-muted-foreground">{p.code}</span>
                  <span
                    className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.16em] ${STATUS_LABEL[p.status].text}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${STATUS_LABEL[p.status].dot}`} />
                    {STATUS_LABEL[p.status].label}
                  </span>
                </div>
                <div className="mt-1 truncate text-[13px] text-foreground">{p.name}</div>
                <div className="mt-2.5 flex items-center gap-2">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-border">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${p.progress}%` }} />
                  </div>
                  <span className="font-mono text-[10px] text-muted-foreground">{p.progress}%</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2.5">
            <span className="mt-0.5 shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] text-primary">
              AI
            </span>
            <p className="text-[11px] leading-relaxed text-fg-2">
              DDT di <span className="text-foreground">Edil Demo</span> letto — 1 quantità diversa
              dall'ordine, da verificare.
            </p>
          </div>
        </div>

        {/* Desktop — full dashboard, tab bar matching the real product */}
        <div className="hidden md:block md:min-h-[480px]">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-background/40 px-4 py-2.5">
            {TABS.map((t, i) => (
              <span
                key={t}
                className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] ${
                  i === 0
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-border text-fg-2'
                }`}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="p-5">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Quadro economico
              </div>
              <Pill accent>Buono</Pill>
            </div>

            <div className="mt-3 flex items-end gap-10">
              <div>
                <div className="text-[40px] font-light leading-none text-success">42,0%</div>
                <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                  consolidato · certo
                </div>
              </div>
              <div>
                <div className="text-[40px] font-light leading-none text-foreground">31,0%</div>
                <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                  in corso · provvisorio
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-4 gap-2">
              <Stat label="Costo a oggi" value="€ 438K" delta="su preventivo" />
              <Stat label="Valore prodotto" value="€ 690K" delta="fatturato € 187K" />
              <Stat label="Avanzamento" value="61%" delta="+4% sett." tone="signal" />
              <Stat label="Margine operativo" value="18,2%" delta="+1,1 pt" tone="signal" />
            </div>

            <div className="mt-5 overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-[110px_1fr_120px_64px_140px] gap-3 border-b border-border bg-secondary px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                <span>Codice</span>
                <span>Commessa</span>
                <span>Fase</span>
                <span>Avanz.</span>
                <span>Budget</span>
              </div>
              {PROJECTS.map((p, i) => (
                <motion.div
                  key={p.code}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 + i * 0.08, duration: 0.5 }}
                  className="grid grid-cols-[110px_1fr_120px_64px_140px] items-center gap-3 border-b border-border px-3 py-2.5 transition-colors last:border-b-0 hover:bg-secondary/60"
                >
                  <span className="font-mono text-[10px] text-muted-foreground">{p.code}</span>
                  <div className="min-w-0">
                    <div className="truncate text-[12px] text-foreground">{p.name}</div>
                    <div className="truncate font-mono text-[9px] uppercase tracking-[0.16em] text-fg-disabled">
                      {p.location}
                    </div>
                  </div>
                  <span className="text-[11px] text-fg-2">{p.phase}</span>
                  <div className="flex items-center gap-1.5">
                    <div className="h-1 w-10 overflow-hidden rounded-full bg-border">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${p.progress}%` }}
                        transition={{ delay: 1.1 + i * 0.08, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full bg-primary"
                      />
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">{p.progress}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] text-fg-2 tabular-nums">{p.budget}</span>
                    <span className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] ${STATUS_LABEL[p.status].text}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_LABEL[p.status].dot}`} />
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.6 }}
              className="mt-4 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2.5"
            >
              <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-primary">
                AI · 14:02
              </span>
              <p className="text-[11px] leading-relaxed text-fg-2">
                DDT n. 4521 di <span className="text-foreground">Edil Demo</span> letto: 3 righe abbinate
                all'ODA 118, 1 quantità diversa dall'ordine. Da verificare prima di imputarlo alla commessa.
                <span className="ml-2 cursor-pointer text-primary underline-offset-2 hover:underline">
                  Apri il DDT ↗
                </span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute -right-4 top-[36%] hidden lg:block"
      >
        <div className="rounded-md border border-input bg-card px-3 py-2 shadow-lg">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-primary">
            Letto dall'AI
          </div>
          <div className="mt-1 text-[11px]">DDT n. 4521 · 22/10</div>
          <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
            3 righe abbinate · 1 da verificare
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Pill({ children, accent }: { children: React.ReactNode; accent?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] ${
        accent
          ? 'border-success/30 bg-success/10 text-success'
          : 'border-border text-muted-foreground'
      }`}
    >
      {children}
    </span>
  );
}

function Stat({
  label,
  value,
  delta,
  tone,
}: {
  label: string;
  value: string;
  delta: string;
  tone?: 'signal' | 'warn';
}) {
  const toneClass =
    tone === 'signal' ? 'text-success' : tone === 'warn' ? 'text-warning' : 'text-muted-foreground';
  return (
    <div className="rounded-lg border border-border bg-secondary p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="boss-h1 mt-1.5 text-[24px] tabular-nums">{value}</div>
      <div className={`mt-1 font-mono text-[9px] uppercase tracking-[0.16em] ${toneClass}`}>{delta}</div>
    </div>
  );
}
